/**
 * Field reports as cards: operator facts, enrichment, and capture geometry.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  MissionFieldReport,
  MissionRouteStatus,
  MissionTimelineStop,
  listMissionFieldReports,
  sendVehicleAlert,
} from '../../services/missionApi';
import { sendCrewMessage } from '../../services/crewChatApi';
import { formatVehicleCrewLine } from './crewChat';
import { buildFieldReportCard } from './fieldReportView';
import FieldReportCard from './FieldReportCard';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FaceIcon from '@mui/icons-material/Face';
import StarIcon from '@mui/icons-material/StarOutline';
import LinkIcon from '@mui/icons-material/Link';
import PromoteToPacketDialog, { PromoteTarget } from '../../components/intel/PromoteToPacketDialog';

export interface MissionPhotosTabProps {
  missionId: number;
  missionName?: string;
  refreshKey?: number;
  timeline?: MissionTimelineStop[] | null;
  units?: 'km' | 'mi';
  canAlert?: boolean;
  onFocus?: (longitude: number, latitude: number) => void;
  onCount?: (count: number) => void;
  onAlerted?: (status: MissionRouteStatus) => void;
}

const MissionPhotosTab: React.FC<MissionPhotosTabProps> = ({
  missionId,
  missionName,
  refreshKey,
  timeline,
  units = 'mi',
  canAlert,
  onFocus,
  onCount,
  onAlerted,
}) => {
  const [reports, setReports] = useState<MissionFieldReport[]>([]);
  const [packetTarget, setPacketTarget] = useState<PromoteTarget | null>(null);
  const [relooking, setRelooking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      listMissionFieldReports(missionId)
        .then((rows) => {
          if (cancelled) return;
          setReports(rows);
          onCount?.(rows.length);
        })
        .catch(() => {
          if (cancelled) return;
          setReports([]);
          onCount?.(0);
        });
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [missionId, refreshKey, onCount]);

  const cards = useMemo(
    () => reports.map((row) => buildFieldReportCard(row, reports, { missionName, timeline, units })),
    [reports, missionName, timeline, units],
  );

  const relook = async (row: MissionFieldReport, label: string) => {
    if (!canAlert) {
      setError('Start the mission to request a re-look on the vehicle');
      return;
    }
    setRelooking(row.id);
    setError(null);
    const message = `Re-look requested — ${label}`;
    try {
      const status = await sendVehicleAlert(missionId, message, 'warning');
      onAlerted?.(status);
      try {
        await sendCrewMessage(formatVehicleCrewLine(message, false), 'general', 'alert');
      } catch {
        /* spoken; Chat is secondary */
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not reach the vehicle');
    } finally {
      setRelooking(null);
    }
  };

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
        Field reports
      </Typography>
      <DockTabHelp tab="photos" />
    </Box>
  );

  if (reports.length === 0) {
    return (
      <Box>
        {title}
        <Typography variant="caption" color="text.secondary">
          No field photos on this run yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title}
      <DockStatStrip
        items={[
          { label: 'Reports', value: reports.length, color: '#e8ebee', icon: <PhotoCameraIcon /> },
          { label: 'Photos', value: reports.filter((r) => r.has_photo).length, color: '#42a5f5', icon: <PhotoCameraIcon /> },
          { label: 'Identified', value: cards.filter((c) => (c.assessed.identified || []).length > 0).length, color: '#66bb6a', icon: <FaceIcon /> },
          { label: 'Priority', value: cards.filter((c) => c.priority === 'PRIORITY').length, color: '#f5b942', icon: <StarIcon /> },
          { label: 'Watchlist', value: reports.filter((r) => r.watchlisted).length, color: '#ef5350' },
          { label: 'Annotated', value: reports.filter((r) => r.has_annotated).length, color: '#ce93d8' },
          { label: 'Kinds', value: new Set(reports.map((r) => (r.kind || 'photo').toLowerCase())).size, color: '#ab47bc' },
          { label: 'Linked', value: cards.filter((c) => c.relatedId).length, color: '#00b8a9', icon: <LinkIcon /> },
        ]}
      />
      {error && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: '#ef5350' }}>
          {error}
        </Typography>
      )}
      {cards.map((model, i) => {
        const row = reports[i];
        return (
          <div
            key={row.id}
            ref={(node) => { cardRefs.current[row.id] = node; }}
          >
            <FieldReportCard
              model={model}
              hasPhoto={row.has_photo}
              hasAnnotated={row.has_annotated}
              reportId={row.id}
              onFocus={() => onFocus?.(row.longitude, row.latitude)}
              onPromote={() => setPacketTarget({
                member_type: 'field_report',
                member_id: row.id,
                label: model.reportId,
                meta: { kind: row.kind, threat_key: `field:${row.id}` },
              })}
              onLinkRelated={model.relatedId ? () => {
                const linked = reports.find((r) => r.id === model.relatedId);
                if (linked) onFocus?.(linked.longitude, linked.latitude);
                cardRefs.current[model.relatedId!]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              } : undefined}
              onRelook={() => relook(row, model.reportId)}
              relooking={relooking === row.id}
            />
          </div>
        );
      })}
      <PromoteToPacketDialog
        open={!!packetTarget}
        onClose={() => setPacketTarget(null)}
        target={packetTarget}
        defaultName={packetTarget?.label}
      />
    </Box>
  );
};

export default MissionPhotosTab;
